import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Fireflies - une prairie d'été la nuit, et des lucioles qui finissent par
// clignoter ensemble.
//
// LA SYNCHRONISATION N'EST PAS SCRIPTÉE, elle ÉMERGE. Chaque luciole est un
// oscillateur de phase avec sa propre fréquence naturelle ; elles se tirent
// mutuellement vers une phase commune, et la force de ce couplage suit
// l'énergie du morceau. Sur un passage calme le couplage est faible et la
// prairie scintille au hasard ; sur un refrain il monte et la prairie se met à
// battre. Personne n'a dit aux lucioles de clignoter sur le temps : elles y
// arrivent. C'est un phénomène réel - plusieurs espèces de Pteroptyx le font -
// et c'est le seul sujet de ce visualiseur.
//
// LE MODÈLE DE KURAMOTO, ET POURQUOI IL EST CALCULABLE. La formule naïve
//   dphi_i/dt = w_i + (K/N) * somme_j sin(phi_j - phi_i)
// demande N² sinus par image : 640 000 pour 800 lucioles, à soixante images
// par seconde. Intenable. Mais la somme se factorise exactement. En posant le
// PARAMÈTRE D'ORDRE
//   r * e^(i*psi) = (1/N) * somme_j e^(i*phi_j)
// qui se calcule en O(N) - un cosinus et un sinus par oscillateur - on a
//   (1/N) * somme_j sin(phi_j - phi_i) = r * sin(psi - phi_i)
// exactement, pas approximativement. Le coût passe de N² à 2N, et `r` est en
// prime la MESURE de la synchronisation : 0 pour le désordre complet, 1 quand
// toutes les phases coïncident. C'est lui que ?fireflyorder=1 affiche.
//
// L'OSCILLATEUR MAÎTRE. Sans lui, la foule converge vers la fréquence MOYENNE
// des lucioles, qui n'est pas le tempo. Un oscillateur invisible battant
// exactement au bpm, couplé à toutes avec un poids double, sert d'ancre : la
// prairie se synchronise sur LUI, donc sur la musique. C'est un Kuramoto forcé,
// et le facteur deux est ce qui lui permet de gagner contre la dispersion des
// fréquences naturelles (+/-8%) dès que le couplage monte.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'fireflies',
  family: 'particles',
  danceability: 0.35,
  energyLevel: 0.32,
  bpmMin: 60,
  bpmMax: 140,
  movement: 0.35,
  tonality: 0.90,
  prefers: { dynamics: [0.35, 1.0] },
  requiresTier: 0,
  enabled: true
};

// --- LE COUPLAGE -----------------------------------------------------------
// K = 0.2 + 2.5 * energy². Le carré est ce qui rend la transition lisible :
// l'ordre de Kuramoto n'apparaît qu'au-dessus d'un seuil critique, et une
// rampe linéaire passait le seuil trop tôt, trop doucement. Au carré, la
// prairie reste désordonnée sur tout le registre calme et bascule franchement
// quand le morceau ouvre.
const K_BASE = 0.2;
const K_GAIN = 2.5;
const K_DROP = 5.0;             // « sur un drop, K saute à 5 »
const DROP_MESURES = 2;
// LE MAÎTRE TIRE DEUX FOIS PLUS FORT QUE LE CHAMP, MAIS SEULEMENT AU-DESSUS DU
// PLANCHER. Un entraînement extérieur toujours actif verrouille une partie de
// la prairie même quand la musique est calme, et c'est exactement ce qu'il ne
// faut pas : mesuré à K = 0.2, le paramètre d'ordre montait à 0.50 - la moitié
// des lucioles accrochées au tempo - là où le désordre vaut 1/racine(N), soit
// 0.035. La raison est arithmétique : à 96 bpm la dispersion des fréquences
// naturelles vaut 0.08 x omega = 0.80 rad/s, et un entraînement de 2 x 0.2 =
// 0.4 rad/s accroche déjà toutes celles qui sont à moins de 4% du tempo, c'est
// à dire la moitié d'une dispersion uniforme à +/-8%.
// En le faisant partir de K_BASE, le maître est MUET sur le registre calme -
// K_BASE est le couplage résiduel, celui qui n'organise jamais rien - et
// domine dès que le morceau ouvre. Les deux termes suivent alors la musique,
// ce qui est tout le propos.
const MAITRE_POIDS = 2.0;
const DISPERSION = 0.08;        // +/-8% autour du tempo
const BPM_DEFAUT_HZ = 1.0;      // sans bpm, le maître bat à 1 Hz
// L'intégration est un Euler explicite. Le pas est BORNÉ : un onglet qui
// revient au premier plan livre un deltaTime de plusieurs secondes, et un pas
// d'Euler de cette taille sur un oscillateur couplé ne converge pas, il
// explose. On plafonne, quitte à ce que la prairie « saute » un instant.
const PAS_MAX = 1 / 30;

// --- LES LUCIOLES ----------------------------------------------------------
const LUCIOLES_MIN = 300;
const TIERS = {
  2: { max: 800, brins: 260, segments: 7, halo: true },
  1: { max: 500, brins: 160, segments: 5, halo: false },
  0: { max: 300, brins: 100, segments: 4, halo: false }
};
const TIER_DEFAUT = 2;

// Le seuil d'allumage : sin(phi) > 0.7 laisse la luciole allumée sur environ
// un quart de son cycle, ce qui est la durée d'un vrai éclair de Photinus.
const SEUIL = 0.7;
// L'enveloppe DANS cette fenêtre. Montée rapide sur les 18 premiers pour cent,
// descente plus lente sur le reste - un éclair de luciole s'allume d'un coup et
// s'éteint en traînant. Une enveloppe symétrique (le sinus lui-même) donne un
// clignotement mou qui ne ressemble à rien.
const MONTEE = 0.18;
const DESCENTE_COURBE = 1.4;
const TAILLE_PX = [3.0, 6.0];
// L'ÉCLAIRAGE LOCAL. Le rayon vient du brief - 40 px - mais le NIVEAU et la
// COURBE sont ce qui décide s'il éclaire l'herbe ou s'il noie la prairie. Avec
// deux cents lucioles allumées à tout instant, deux cents disques de 40 px
// couvrent plus que l'écran : le profil doit tomber très vite, sans quoi leur
// somme devient une nappe verte uniforme et le clignotement de chacune
// disparaît dedans. Mesuré avant correction : prairie à L=43 pour un ciel à
// 4.4, et « presque toutes brillent en même temps » alors que la fraction
// allumée était bonne (0.253).
const HALO_PX = 40.0;
const HALO_NIVEAU = 0.055;
const HALO_COURBE = 6.0;
// Où passe la coupure entre « lointaine » et « proche » : en dessous, la
// luciole est dessinée derrière l'herbe.
const PROF_COUPURE = 0.45;

// LA BIOLUMINESCENCE EST UNE COULEUR PHYSIQUE. Le pic d'émission de la
// luciférine est à 560 nm - un jaune-vert. Il ne dépend pas de la pochette, et
// c'est exactement pourquoi la palette ne le touche jamais : une luciole
// bleue n'existe pas. La palette teinte le ciel et la brume, rien d'autre.
const CORPS = [1.00, 0.97, 0.72];   // le coeur, presque blanc-jaune
const LUEUR = [0.62, 1.00, 0.28];   // le halo, franchement vert

// --- LA PRAIRIE ------------------------------------------------------------
// « Herbes HAUTES au premier plan » : les brins montent jusqu'aux trois quarts
// de la moitié basse du cadre et croisent donc la zone où vivent les lucioles.
// À 0.18-0.52 ils restaient une frange en bas de l'image.
const HERBE_HAUT = [0.30, 0.86];    // en unités de hauteur, depuis le bas
const HERBE_LARGE = 0.009;
const VENT_BASE = 0.35, VENT_GAIN = 1.5;
const HORIZON = -0.28;              // la ligne des arbres
// LA BRUME EST UNE BANDE, PAS UN CHAMP. Étalée sur toute la moitié basse, elle
// faisait de la prairie la zone la plus CLAIRE du cadre - mesuré L=43 contre
// un ciel à 4.4 - et les lucioles, qui sont des points de lumière, n'avaient
// plus de noir sur quoi se détacher. Elle se serre maintenant contre
// l'horizon, là où les lointaines doivent se diluer, et le sol en dessous est
// plus sombre que le ciel.
const BRUME_HAUT = 0.07;            // au-dessus de l'horizon
// La brume descend assez bas pour que les brins d'herbe hautes aient de quoi
// se DÉCOUPER dessus : une silhouette sur du noir n'est rien du tout, et
// c'était le seul moyen de voir l'herbe sans l'éclairer par en dessous.
const BRUME_BAS = 0.26;             // en dessous
const BRUME_FORCE = 0.55;
// LES ÉTOILES. Leur pic valait 0,5 en linéaire sur un ciel qui en vaut environ
// 0,001 : après ACES et la courbe sRGB, des points blancs à 185/255 sur un ciel
// à 5. Ce sont elles que le code appelle déjà « scint », et elles écrasaient
// tout le reste de la nuit.
const ETOILE_NIVEAU = 0.16;
// LES ÉTOILES FILANTES. Une seule à la fois, et une attente longue entre deux :
// une filante toutes les trois secondes n'est plus un événement, c'est une
// pluie. Sa course, sa traîne et son épaisseur sont en unités de la scène.
const FILANTE_DUREE = 0.62;
const FILANTE_COURSE = 1.15;
const FILANTE_TRAINEE = 0.24;
const FILANTE_EPAISSEUR = 0.011;
const FILANTE_NIVEAU = 0.42;
const FILANTE_ATTENTE = [7.0, 20.0];
// La ligne d'arbres précalculée : une texture d'une ligne, trois canaux.
// 8192 colonnes sur 7 unités : sur une dalle 2560 l'écran en montre environ
// 3,6, soit 4200 texels pour 2560 pixels. Au-dessous, l'interpolation linéaire
// entre deux texels arrondit la silhouette et on voit l'escalier.
const ARBRES_LARGEUR = 8192;
const ARBRES_PORTEE = 3.5;

const PLANCHER_PARTAGE = 0.32;
const PLANCHER = 0.50;
const CRETE = 0.94;
const LUMA = 'vec3(0.2125, 0.7154, 0.0721)';

function urlParam(nom) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(nom);
}
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

function versLineaire(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
// THREE.ColorManagement est désactivé dans ce build : setHSL y écrit du sRGB
// brut et une couleur arrive 3,5 fois trop claire.
function poserHSL(couleur, h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 1) + 1) % 1) * 6;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0, g = 0, b = 0;
  if (hp < 1) { r = c; g = x; } else if (hp < 2) { r = x; g = c; }
  else if (hp < 3) { g = c; b = x; } else if (hp < 4) { g = x; b = c; }
  else if (hp < 5) { r = x; b = c; } else { r = c; b = x; }
  const m = l - c / 2;
  couleur.setRGB(versLineaire(r + m), versLineaire(g + m), versLineaire(b + m));
  return couleur;
}
function teinteDe(hex) {
  const r = ((hex >> 16) & 255) / 255, g = ((hex >> 8) & 255) / 255, b = (hex & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  if (d < 1e-6) return null;
  let h;
  if (mx === r) h = ((g - b) / d + (g < b ? 6 : 0)); else if (mx === g) h = (b - r) / d + 2; else h = (r - g) / d + 4;
  return h / 6;
}

const ZONE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  float zoneSecurite(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float a = mix(${PLANCHER_PARTAGE.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
    return ${PLANCHER.toFixed(2)} + (a - ${PLANCHER_PARTAGE.toFixed(2)})
         * ${((1 - PLANCHER) / (1 - PLANCHER_PARTAGE)).toFixed(6)};
  }
`;

const HASH_CHUNK = `
  // Les entrées sont bornées avant le sinus : sur la TV, mediump est du vrai
  // fp16 et fract(sin(x) * 43758) s'effondre en bandes plates dès que x passe
  // la seizaine.
  float hash21(vec2 p) {
    return fract(sin(mod(dot(p, vec2(12.9898, 78.233)), 6283.1853)) * 43758.5453);
  }
`;

// ---------------------------------------------------------------------------
// LE FOND : ciel, étoiles, lune, arbres, brume
// ---------------------------------------------------------------------------
const fondVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

function fondFragmentShader(opts) {
  return `
  precision mediump float;

  uniform vec3 uCiel;        // la palette, très désaturée
  uniform vec3 uCielHaut;
  uniform vec3 uBrume;
  uniform vec3 uSol;
  uniform float uTemps;
  uniform vec4 uFilante;     // tete.xy, direction.xy
  uniform float uFilanteVie; // 0 = aucune filante en cours
  uniform float uOrdre;      // ?fireflyorder=1 : la barre du paramètre d'ordre
  uniform float uOrdreVu;

  varying vec2 vUv;

  ${ZONE_CHUNK}
  ${HASH_CHUNK}

  // LA LIGNE D'ARBRES EST PRÉCALCULÉE. Elle ne bouge jamais : sa forme ne
  // dépend ni du temps, ni de la musique, ni de rien d'autre que d'un
  // hachage. La calculer par pixel et par image revenait à neuf profils et
  // vingt-sept hachages sur chaque fragment de la bande d'horizon - mesuré
  // sur le Pi, le temps d'image passait de 12 à 29 ms, et les trois tiers
  // rendaient le même chiffre, ce qui désigne toujours un coût commun.
  // Elle vit maintenant dans une texture d'une ligne : trois canaux, une
  // hauteur de cime par plan de profondeur, et le fragment n'a plus qu'un
  // échantillonnage à faire.
  uniform sampler2D uArbres;
  uniform float uArbresPortee;   // demi-largeur du domaine baké, en unités

  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uAspect, ndc.y);

    // LE CIEL. Un dégradé vertical, sombre partout : c'est une nuit d'été, pas
    // un crépuscule. La palette ne fait que le TEINTER.
    float t = clamp((s.y + 1.0) * 0.5, 0.0, 1.0);
    // L'exposant est 1.5 et non 2 : au carré, le ciel atteignait sa teinte la
    // plus sombre dès le tiers de la hauteur et le dégradé ne se lisait plus.
    vec3 col = mix(uCiel, uCielHaut, pow(t, 1.5));

    // LES ÉTOILES. Une grille de hachage, dont on ne garde que la queue haute :
    // beaucoup de cases, très peu d'étoiles, et leur scintillement est lent.
    vec2 g = s * 26.0;
    vec2 cellule = floor(g);
    float h = hash21(cellule);
    if (h > 0.986 && s.y > ${(HORIZON + 0.05).toFixed(3)}) {
      vec2 dans = fract(g) - vec2(0.5 + (hash21(cellule + 3.1) - 0.5) * 0.6,
                                  0.5 + (hash21(cellule + 7.7) - 0.5) * 0.6);
      float d = length(dans);
      float scint = 0.65 + 0.35 * sin(uTemps * (0.7 + h * 2.0) + h * 40.0);
      col += vec3(0.75, 0.80, 0.95) * (1.0 - smoothstep(0.0, 0.14, d)) * ${ETOILE_NIVEAU.toFixed(2)} * scint;
    }

    // L'ÉTOILE FILANTE. Le CPU décide s'il y en a une, où est sa tête et dans
    // quel sens elle va ; le fragment ne fait que dessiner le segment. Tirer
    // la filante au hachage DANS le shader aurait coûté deux sinus par pixel
    // de ciel, et la ligne d'arbres a déjà montré ce que ça vaut sur le Pi.
    // Ici : une projection sur la direction, une longueur, et c'est tout.
    if (uFilanteVie > 0.0 && s.y > ${(HORIZON + 0.05).toFixed(3)}) {
      vec2 tete = uFilante.xy, dir = uFilante.zw;
      // Position LE LONG de la traîne, derrière la tête, jamais devant.
      float le = clamp(dot(s - tete, -dir), 0.0, ${FILANTE_TRAINEE.toFixed(2)});
      float d = length(s - (tete - dir * le));
      float noyau = 1.0 - smoothstep(0.0, ${FILANTE_EPAISSEUR.toFixed(3)}, d);
      // Le carré des deux : un noyau net et une traîne qui s'éteint vite.
      float fuseau = 1.0 - le / ${FILANTE_TRAINEE.toFixed(2)};
      col += vec3(0.86, 0.90, 1.00)
           * (noyau * noyau) * (fuseau * fuseau)
           * uFilanteVie * ${FILANTE_NIVEAU.toFixed(2)};
    }

    // LA LUNE, basse et à gauche. Un disque doux, plus son halo dans l'air.
    vec2 lune = vec2(-uAspect * 0.58, ${(HORIZON + 0.30).toFixed(3)});
    float dl = length(s - lune);
    col += vec3(0.80, 0.84, 0.92) * (1.0 - smoothstep(0.0, 0.055, dl)) * 0.55;
    col += vec3(0.42, 0.48, 0.62) * pow(max(0.0, 1.0 - dl / 0.85), 3.0) * 0.16;

    // LES ARBRES, en silhouette. Presque noirs, très légèrement teintés du
    // ciel : une silhouette parfaitement noire se décolle du fond et fait
    // découpage.
    //
    // LA CIME N'EST CALCULÉE QU'AUTOUR DE L'HORIZON. Elle coûte quatre sinus et
    // deux puissances fractionnaires, et elle ne décide de rien au-delà de
    // quelques dixièmes d'unité de part et d'autre de la ligne : au-dessus le
    // ciel gagne toujours, en dessous les arbres toujours. Sur les dalles du
    // Pi, ces deux puissances tournaient sur un demi-million de pixels pour n'en
    // changer que quelques milliers.
    // TROIS PLANS DE PROFONDEUR, du plus lointain au plus proche, chacun un
    // peu plus sombre : c'est ce dégradé entre les plans qui donne l'épaisseur
    // du bois. Calculés seulement autour de l'horizon - au-dessus le ciel gagne
    // toujours, en dessous le sol - parce que chaque plan coûte trois hachages
    // et un profil, et qu'ils ne décident de rien ailleurs.
    if (s.y < ${(HORIZON + 0.40).toFixed(3)} && s.y > ${(HORIZON - 0.06).toFixed(3)}) {
      float y0 = s.y - ${HORIZON.toFixed(3)};
      // Les trois hauteurs de cime, d'un seul échantillonnage. Le canal r est
      // le plan du fond, b le plus proche.
      vec3 cimes = texture2D(uArbres, vec2((s.x / uArbresPortee) * 0.5 + 0.5, 0.5)).rgb * 0.5;
      float a3 = smoothstep(0.004, -0.004, y0 - cimes.r);
      float a2 = smoothstep(0.004, -0.004, y0 - cimes.g);
      float a1 = smoothstep(0.004, -0.004, y0 - cimes.b);
      // Trois teintes bien SÉPARÉES : à 0.34 / 0.22 / 0.13 les plans se
      // confondaient et le bois n'avait pas d'épaisseur.
      col = mix(col, uCiel * 0.52, a3);   // le fond, presque dans la brume
      col = mix(col, uCiel * 0.26, a2);
      col = mix(col, uCiel * 0.09, a1);   // le plus proche, le plus noir
    } else if (s.y <= ${(HORIZON - 0.06).toFixed(3)}) {
      col = mix(col, uCiel * 0.13, 1.0);
    }

    // LE SOL. Bleu-noir, PLUS SOMBRE QUE LE CIEL : la prairie n'est pas une
    // surface éclairée, c'est ce qui reste quand il n'y a plus de ciel. Les
    // lucioles sont les seules sources de lumière de cette image, et elles ne
    // peuvent l'être que sur du noir.
    float sol = smoothstep(${(HORIZON + 0.010).toFixed(3)}, ${(HORIZON - 0.010).toFixed(3)}, s.y);
    col = mix(col, uSol, sol);

    ${opts.brume ? `
    // LA BRUME, EN BANDE SERRÉE SUR L'HORIZON. C'est là et seulement là que les
    // lucioles lointaines doivent se diluer ; plus bas, le sol reste noir.
    // LE BORD INFÉRIEUR S'ÉTEINT SUR TOUTE LA HAUTEUR DE LA BANDE, pas sur un
    // tiers : à ce rythme-là la brume se terminait par une ligne horizontale
    // nette, qui se lit comme une bande collée sur l'image et non comme de la
    // brume. Au carré, elle s'efface d'autant plus vite qu'on descend.
    float bBas = smoothstep(${(HORIZON - BRUME_BAS).toFixed(3)}, ${(HORIZON + 0.02).toFixed(3)}, s.y);
    float b = smoothstep(${(HORIZON + BRUME_HAUT).toFixed(3)}, ${HORIZON.toFixed(3)}, s.y) * bBas * bBas;
    float ondule = 0.82 + 0.18 * sin(s.x * 1.9 + uTemps * 0.13);
    col = mix(col, uBrume, b * ${BRUME_FORCE.toFixed(2)} * ondule);
    ` : ''}

    // PAS DE ZONE DE SÉCURITÉ SUR LE FOND, et c'est délibéré. Elle existe pour
    // empêcher ce qui BOUGE de concurrencer la pochette ; un ciel immobile ne
    // concurrence rien, et la pochette est posée dessus de toute façon. Sur un
    // dégradé aussi lisse, la multiplication dessinait au contraire un
    // rectangle sombre parfaitement visible au milieu du ciel. Les lucioles et
    // les herbes, elles, l'appliquent.

    // ?fireflyorder=1 : le paramètre d'ordre, en barre, en bas du cadre. Pas
    // un chiffre - il n'y a pas de police ici - mais une longueur, et c'est
    // une longueur qu'on regarde monter.
    if (uOrdreVu > 0.5) {
      float y0 = -0.94, y1 = -0.90;
      float x0 = -uAspect * 0.9, x1 = x0 + uAspect * 1.8 * uOrdre;
      if (s.y > y0 && s.y < y1 && s.x > x0 && s.x < x1) col = vec3(0.9, 0.2, 0.2);
      if (s.y > y0 && s.y < y1 && abs(s.x - (x0 + uAspect * 1.8)) < 0.004) col = vec3(0.5);
    }

    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;
}

// ---------------------------------------------------------------------------
// LES LUCIOLES : un THREE.Points, la phase en attribut
// ---------------------------------------------------------------------------
function lucioleVertexShader(opts) {
  return `
  precision mediump float;

  attribute float aPhi;      // la phase, seul attribut réécrit par image
  attribute float aProf;     // 0 au fond, 1 au premier plan
  attribute float aSeed;
  attribute float aVivante;  // 0 quand la luciole est hors du compte courant

  uniform float uTemps;
  uniform float uTaillePx;
  uniform float uDpr;
  uniform vec2 uProfBande;   // quelle tranche de profondeur cette passe dessine

  varying float vNiveau;
  varying float vProf;
  varying float vAtt;

  ${ZONE_CHUNK}

  // Un champ tourbillonnant lisse, PAS un vrai curl : trois produits
  // sinus/cosinus incommensurables, sans divergence à l'oeil, pour une
  // fraction du coût d'un gradient simplex. La dérive est BORNÉE plutôt
  // qu'intégrée - une luciole qui intègre finit par sortir du cadre, et il
  // faudrait la replier, ce qui se voit.
  vec2 derive(vec2 p, float t) {
    return vec2(sin(p.y * 1.7 + t * 0.21) * cos(p.x * 1.3 - t * 0.17),
                sin(p.x * 1.1 - t * 0.19) * cos(p.y * 1.9 + t * 0.23));
  }

  void main() {
    if (aVivante < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vNiveau = 0.0; return; }
    // DEUX PASSES, UNE GÉOMÉTRIE. Les lointaines passent DERRIÈRE l'herbe du
    // premier plan, les proches DEVANT - c'est ce qui fait qu'un brin coupe une
    // luciole du fond et pas une du bord. Une seule passe les mettait toutes
    // du même côté, et la prairie redevenait plate.
    if (aProf < uProfBande.x || aProf >= uProfBande.y) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vNiveau = 0.0; return; }

    // L'ENVELOPPE DE L'ÉCLAIR. Allumée quand sin(phi) dépasse le seuil, soit
    // environ un quart du cycle ; dans cette fenêtre, montée rapide puis
    // descente traînante.
    float sp = sin(aPhi);
    if (sp <= ${SEUIL.toFixed(2)}) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vNiveau = 0.0; return; }
    float phiSeuil = asin(${SEUIL.toFixed(2)});
    float pos = clamp((mod(aPhi, 6.2831853) - phiSeuil) / (3.14159265 - 2.0 * phiSeuil), 0.0, 1.0);
    float env = pos < ${MONTEE.toFixed(2)}
      ? pos / ${MONTEE.toFixed(2)}
      : pow(1.0 - (pos - ${MONTEE.toFixed(2)}) / ${(1 - MONTEE).toFixed(2)}, ${DESCENTE_COURBE.toFixed(1)});

    // La position : un point de base tiré une fois, plus une dérive bornée.
    vec2 base = position.xy;
    vec2 p = base + derive(base * 1.6 + aSeed * 13.0, uTemps) * (0.055 + 0.045 * aProf);
    // LA DÉRIVE EST BORNÉE EN HAUTEUR. Le point de départ est déjà sous la
    // ligne d'arbres ; sans ce plafond, la dérive l'y repousse au bout de
    // quelques secondes et la luciole part dans le ciel.
    p.y = min(p.y, ${(HORIZON + 0.11).toFixed(3)});
    vNiveau = env;
    vProf = aProf;
    vAtt = zoneSecurite(p);
    gl_Position = vec4(p.x / uAspect, p.y, 0.0, 1.0);
    // Les lointaines sont plus petites. La taille est en pixels d'écran, donc
    // multipliée par le rapport de pixels - sans quoi une luciole fait la
    // moitié de sa taille sur une dalle à deux pixels par point.
    // LES LOINTAINES SONT TROIS FOIS PLUS PETITES. C'est la profondeur : à
    // taille égale, huit cents points se lisent comme un seul plan.
    gl_PointSize = uTaillePx * mix(0.33, 1.0, aProf) * uDpr * (0.55 + 0.45 * env);
  }
`;
}

function lucioleFragmentShader(opts) {
  return `
  precision mediump float;

  uniform vec3 uCorps;
  uniform vec3 uLueur;
  uniform float uNiveau;

  varying float vNiveau;
  varying float vProf;
  varying float vAtt;

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0;
    if (d > 1.0) discard;
    // Un coeur presque blanc-jaune dans un halo court franchement vert : c'est
    // le profil d'une vraie luciole photographiée, et c'est le halo qui porte
    // la couleur, pas le centre.
    float coeur = 1.0 - smoothstep(0.0, 0.45, d);
    float halo = pow(max(0.0, 1.0 - d), 2.2);
    vec3 col = uCorps * coeur + uLueur * halo * 0.55;
    // ET DEUX FOIS PLUS FAIBLES. Avec la brume qui les recouvre sur l'horizon,
    // c'est ce qui les met au fond.
    col *= vNiveau * uNiveau * mix(0.50, 1.0, vProf) * vAtt;
    float lum = dot(col, ${LUMA});
    if (lum > ${CRETE.toFixed(2)}) col *= ${CRETE.toFixed(2)} / lum;
    gl_FragColor = vec4(col, 1.0);
  }
`;
}

// L'ÉCLAIRAGE LOCAL : le même nuage de points, en beaucoup plus large et
// beaucoup plus faible, dessiné AVANT les herbes. Les brins étant des
// silhouettes quasi noires, c'est la lueur qui passe AUTOUR d'eux qui les fait
// lire comme éclairés - il n'y a pas de modèle d'éclairage ici, et il n'en
// faut pas.
const haloFragmentShader = `
  precision mediump float;
  uniform vec3 uLueur;
  varying float vNiveau;
  varying float vProf;
  varying float vAtt;
  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0;
    if (d > 1.0) discard;
    float a = pow(max(0.0, 1.0 - d), ${HALO_COURBE.toFixed(1)});
    gl_FragColor = vec4(uLueur * a * vNiveau * ${HALO_NIVEAU.toFixed(3)} * mix(0.25, 1.0, vProf) * vAtt, 1.0);
  }
`;

// ---------------------------------------------------------------------------
// LES HERBES : des fat-lines, un brin par instance
// ---------------------------------------------------------------------------
const herbeVertexShader = `
  precision mediump float;

  // L'attribut position n'est PAS déclaré ici : three.js injecte toujours
  // "attribute vec3 position;" dans le prélude d'un Mesh, et le redéclarer fait
  // échouer la compilation sur « redefinition » - le programme est alors
  // abandonné en silence et la couche ne dessine rien. On s'en sert tel quel :
  // x vaut -1 ou 1 (le côté du brin), y va de 0 à 1 le long du brin.
  // (Et pas d'accent grave dans ce commentaire : il vit dans un template
  // literal JS, un accent grave le fermerait et esbuild échouerait sur une
  // erreur qui ne parle pas de shader.)
  attribute float aX;        // la base, en unités de hauteur
  attribute float aHaut;
  attribute float aPenche;
  attribute float aSeed;
  attribute float aProf;
  attribute float aLarge;

  uniform float uTemps;
  uniform float uVent;

  varying float vProf;
  varying float vT;

  ${ZONE_CHUNK}

  void main() {
    float t = position.y;
    // LA COURBURE. Un brin d'herbe ne plie pas comme un pendule : il plie de
    // plus en plus vers la pointe, d'où le t². Le vent s'ajoute au même
    // endroit, avec une phase par brin - sans elle toute la prairie ondule
    // comme un seul objet.
    float souffle = sin(uTemps * (0.8 + aSeed * 0.6) + aX * 2.3 + aSeed * 6.0);
    float courbe = (aPenche + souffle * uVent * 0.09) * t * t;
    vec2 centre = vec2(aX + courbe, -1.0 + aHaut * t);
    // La largeur s'annule à la pointe : c'est ce qui en fait un brin et non
    // une bande.
    float demi = ${HERBE_LARGE.toFixed(4)} * (1.0 - t) * mix(0.6, 1.25, aProf) * aLarge;
    vec2 p = centre + vec2(position.x * demi, 0.0);
    vProf = aProf;
    vT = t;
    gl_Position = vec4(p.x / uAspect, p.y, 0.0, 1.0);
  }
`;

const herbeFragmentShader = `
  precision mediump float;
  uniform vec3 uHerbe;
  varying float vProf;
  varying float vT;
  void main() {
    // Presque noire, et d'autant plus que le brin est proche : le premier plan
    // est ce qui doit se découper le plus franchement.
    vec3 col = uHerbe * mix(0.55, 0.16, vProf) * (0.75 + 0.25 * vT);
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class FirefliesVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : TIER_DEFAUT;
    this._tier = TIERS[tier] ? tier : TIER_DEFAUT;
    this._opts = TIERS[this._tier];

    // ?fireflyk=<f> force le couplage.
    const k = parseFloat(urlParam('fireflyk') || '');
    this._kForce = isFinite(k) ? k : null;
    // ?fireflyorder=1 affiche le paramètre d'ordre en barre.
    this._voirOrdre = urlParam('fireflyorder') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    scene.fog = null;

    const N = this._opts.max;
    this._N = N;

    this._uniforms = {
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather,
      uTemps: { value: 0 },
      uFilante: { value: new THREE.Vector4(0, 0, 1, 0) },
      uFilanteVie: { value: 0 },
      uCiel: { value: new THREE.Color() },
      uCielHaut: { value: new THREE.Color() },
      uBrume: { value: new THREE.Color() },
      uSol: { value: new THREE.Color() },
      uHerbe: { value: new THREE.Color() },
      uCorps: { value: new THREE.Color(versLineaire(CORPS[0]), versLineaire(CORPS[1]), versLineaire(CORPS[2])) },
      uLueur: { value: new THREE.Color(versLineaire(LUEUR[0]), versLineaire(LUEUR[1]), versLineaire(LUEUR[2])) },
      uNiveau: { value: 1 },
      uTaillePx: { value: TAILLE_PX[1] },
      uHaloPx: { value: HALO_PX },
      uDpr: { value: 1 },
      uVent: { value: VENT_BASE },
      uProfBande: { value: new THREE.Vector2(0, 1) },
      uOrdre: { value: 0 },
      uOrdreVu: { value: this._voirOrdre ? 1 : 0 },
      uArbres: { value: null },
      uArbresPortee: { value: ARBRES_PORTEE }
    };

    // --- L'ÉTAT DE KURAMOTO ---
    this._phi = new Float32Array(N);
    this._omega = new Float32Array(N);
    this._phiAttr = new Float32Array(N);
    this._vivante = new Float32Array(N);
    this._graine = 9;
    for (let i = 0; i < N; i++) this._phi[i] = this._hasard() * Math.PI * 2;
    this._phiMaitre = 0;
    this._r = 0;
    this._psi = 0;
    this._K = K_BASE;
    this._dropJusqu = -1;

    this._arbresTex = this._bakerArbres();
    this._uniforms.uArbres.value = this._arbresTex;
    this._construireFond();
    this._construireLucioles();
    this._construireHerbes();

    this._temps = 0;
    this._basse = 0.4;
    this._medium = 0.4;
    this._energie = 0.3;
    this._paletteVue = undefined;
    this._dernierMode = null;
    this._dernierTrace = 0;
    // L'ÉTOILE FILANTE : -1 = aucune en cours, sinon l'avancement 0..1.
    // ?ffilante=1 les enchaîne, sinon on ne peut pas en capturer une : elles
    // sont rares par construction, et attendre le hasard n'est pas une
    // méthode de vérification.
    this._filanteU = -1;
    this._filanteEnchaine = urlParam('ffilante') === '1';
    this._filanteAttente = this._filanteEnchaine ? 0.2 : FILANTE_ATTENTE[0];
    this._filanteP0 = { x: 0, y: 0 };
    this._filanteDir = { x: 1, y: 0 };
    this._appliquerPalette(null);
  }

  // L'ÉTOILE FILANTE, décidée ici et non dans le shader. Elle part du ciel
  // HAUT et descend : une filante qui monte n'existe pas. L'angle reste entre
  // 25 et 55 degrés sous l'horizontale - au-delà elle tombe comme une pierre,
  // en deçà elle rase le ciel sans jamais le traverser.
  _majFilante(dt) {
    const u = this._uniforms;
    if (this._filanteU >= 0) {
      this._filanteU += dt / FILANTE_DUREE;
      if (this._filanteU > 1) {
        this._filanteU = -1;
        u.uFilanteVie.value = 0;
        const [a, b] = FILANTE_ATTENTE;
        this._filanteAttente = this._filanteEnchaine ? 0.2 : a + this._hasard() * (b - a);
      } else {
        const p = this._filanteP0, d = this._filanteDir, k = this._filanteU * FILANTE_COURSE;
        u.uFilante.value.set(p.x + d.x * k, p.y + d.y * k, d.x, d.y);
        // Elle s'allume et s'éteint en demi-sinusoïde : apparaître d'un coup
        // ressemble à un défaut d'affichage, pas à une étoile.
        u.uFilanteVie.value = Math.sin(this._filanteU * Math.PI);
      }
      return;
    }
    this._filanteAttente -= dt;
    if (this._filanteAttente > 0) return;
    const asp = safeZoneUniforms.uAspect.value || 1.78;
    this._filanteP0.x = (this._hasard() * 2 - 1) * asp * 0.95;
    this._filanteP0.y = 0.45 + this._hasard() * 0.53;
    const ang = 0.44 + this._hasard() * 0.52;
    const sens = this._hasard() < 0.5 ? -1 : 1;
    this._filanteDir.x = sens * Math.cos(ang);
    this._filanteDir.y = -Math.sin(ang);
    this._filanteU = 0;
  }

  _hasard() {
    this._graine = (this._graine * 1103515245 + 12345) % 2147483648;
    return this._graine / 2147483648;
  }

  // LE BAKE DE LA LIGNE D'ARBRES. Le même calcul que la version shader, fait
  // une fois : chaque plan est une suite de cases, chaque case porte un arbre
  // dont la position dans sa case, la largeur, la hauteur et la forme sortent
  // d'un hachage de son indice - donc espacements inégaux, hauteurs du simple
  // au double, et trois profils (conifère pointu, feuillu rond, vieux chêne
  // étalé) mélangés par un hachage INDÉPENDANT de la position, sans quoi la
  // rangée reprend un motif.
  _bakerArbres() {
    const L = ARBRES_LARGEUR;
    const data = new Uint8Array(L * 4);
    // `mod` du GLSL, PAS le `%` de JavaScript : pour un argument négatif -
    // c'est-à-dire toute la moitié gauche de l'écran - `%` garde le signe et
    // `mod` non. Les deux moitiés du bois ne suivaient pas la même loi.
    const hash = (a, b) => {
      const d = a * 12.9898 + b * 78.233, m = 6283.1853;
      const x = Math.sin(d - m * Math.floor(d / m)) * 43758.5453;
      return x - Math.floor(x);
    };
    const profil = (u, f) => {
      const a = Math.abs(u);
      const rond = Math.sqrt(Math.max(0, 1 - a * a));
      const pointu = Math.max(0, 1 - a);
      const etale = Math.pow(Math.max(0, 1 - a * a), 0.35);
      return f < 0.5 ? pointu + (rond - pointu) * f * 2 : rond + (etale - rond) * (f - 0.5) * 2;
    };
    const rideau = (x, pas, ech, dec) => {
      let h = 0;
      const c = Math.floor(x / pas + dec);
      for (let k = -1; k <= 1; k++) {
        const ci = c + k;
        const g1 = hash(ci, dec * 7), g2 = hash(ci + 31, dec * 13), g3 = hash(ci + 57, dec * 3);
        const cx = (ci + 0.15 + 0.7 * g1) * pas;
        // 0,55 à 1,40 fois le pas : les couronnes SE CHEVAUCHENT et le bois
        // est continu. Transcrit à 0,28-0,70 lors du précalcul, il devenait
        // une rangée de petites bosses séparées - c'est la seule chose qui
        // avait changé entre la version calculée par pixel et la version
        // précalculée, et elle suffisait à défaire le décor.
        const larg = pas * (0.55 + 0.85 * g2);
        const haut = ech * (0.55 + 1.10 * g3);
        const u = (x - cx) / larg;
        // La forme est tirée de g1, le même hachage que la position.
        if (Math.abs(u) < 1) h = Math.max(h, profil(u, g1) * haut);
      }
      return h;
    };
    for (let i = 0; i < L; i++) {
      const x = ((i + 0.5) / L * 2 - 1) * ARBRES_PORTEE;
      // Encodé sur 0..0.5 unité de hauteur : un octet y vaut 1/512e d'unité,
      // soit moins d'un demi-pixel de cime sur une dalle 720p.
      data[i * 4] = Math.round(Math.min(1, rideau(x, 0.21, 0.135, 0.0) / 0.5) * 255);
      data[i * 4 + 1] = Math.round(Math.min(1, rideau(x, 0.33, 0.195, 5.3) / 0.5) * 255);
      data[i * 4 + 2] = Math.round(Math.min(1, rideau(x, 0.52, 0.270, 11.7) / 0.5) * 255);
      data[i * 4 + 3] = 255;
    }
    const t = new THREE.DataTexture(data, L, 1, THREE.RGBAFormat);
    t.colorSpace = THREE.NoColorSpace;   // des hauteurs, pas des couleurs
    t.minFilter = THREE.LinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.wrapS = THREE.ClampToEdgeWrapping;
    t.wrapT = THREE.ClampToEdgeWrapping;
    t.generateMipmaps = false;
    t.needsUpdate = true;
    return t;
  }

  _construireFond() {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
    g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    this._fondGeo = g;
    this._fondMat = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: fondVertexShader,
      fragmentShader: fondFragmentShader({ brume: true }),
      depthTest: false, depthWrite: false, transparent: false
    });
    this._fond = new THREE.Mesh(g, this._fondMat);
    this._fond.frustumCulled = false;
    this._fond.renderOrder = 0;
    this._scene.add(this._fond);
  }

  _construireLucioles() {
    const N = this._N;
    const pos = new Float32Array(N * 3);
    const prof = new Float32Array(N);
    const seed = new Float32Array(N);
    const asp = 1.9;   // une base large : l'aspect réel est appliqué au rendu
    for (let i = 0; i < N; i++) {
      prof[i] = this._hasard();
      // Les lointaines sont plus hautes et plus resserrées vers l'horizon, les
      // proches plus basses et plus étalées : c'est toute la perspective dont
      // une prairie a besoin.
      // LE VOLUME DE LA PRAIRIE, ET IL EST BORNÉ EN HAUT. Une luciole vit du
      // sol jusqu'à une fois et demie la hauteur de l'herbe, et JAMAIS au-dessus
      // de la ligne d'arbres - mesuré sur la version précédente, 109 sur 800
      // flottaient dans le ciel, ce qui n'est pas une prairie mais des étoiles
      // vertes. Trois pour cent montent jusqu'aux cimes, et c'est tout ce que
      // le hasard a le droit de faire ici.
      const herbeIci = HERBE_HAUT[0] + (HERBE_HAUT[1] - HERBE_HAUT[0]) * (0.55 + 0.45 * prof[i]);
      const plafond = Math.min(HORIZON - 0.02, -1 + herbeIci * 1.4);
      const haut = this._hasard() < 0.03 ? HORIZON + 0.11 : plafond;
      pos[i * 3 + 1] = -1 + (haut + 1) * Math.pow(this._hasard(), 0.75);
      pos[i * 3] = (this._hasard() * 2 - 1) * asp * (0.6 + 0.4 * prof[i]);
      pos[i * 3 + 2] = 0;
      seed[i] = this._hasard();
      // La fréquence naturelle : le tempo, dispersé de +/-8%. Posée ici en
      // FRACTION, multipliée par la pulsation du maître à chaque image - le
      // tempo change d'un morceau à l'autre, la dispersion non.
      this._omega[i] = 1 + (this._hasard() * 2 - 1) * DISPERSION;
      this._vivante[i] = 1;
    }
    // La dispersion RÉELLEMENT obtenue, exposée pour la sonde : c'est elle qui
    // fixe le couplage critique, et la supposer est le meilleur moyen de
    // chercher un défaut là où il n'est pas.
    let mn = 9, mx = -9, sm = 0;
    for (let i = 0; i < N; i++) { if (this._omega[i] < mn) mn = this._omega[i]; if (this._omega[i] > mx) mx = this._omega[i]; sm += this._omega[i]; }
    this._omegaSpread = { min: mn, max: mx, moyenne: sm / N };
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('aProf', new THREE.BufferAttribute(prof, 1));
    g.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    g.setAttribute('aPhi', new THREE.BufferAttribute(this._phiAttr, 1));
    g.setAttribute('aVivante', new THREE.BufferAttribute(this._vivante, 1));
    this._lucGeo = g;

    this._lucMat = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: lucioleVertexShader(this._opts),
      fragmentShader: lucioleFragmentShader(this._opts),
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this._lucioles = new THREE.Points(g, this._lucMat);
    this._lucioles.frustumCulled = false;
    this._lucioles.renderOrder = 4;   // les proches, DEVANT l'herbe
    this._lucMat.uniforms = Object.assign({}, this._uniforms,
      { uProfBande: { value: new THREE.Vector2(PROF_COUPURE, 1.01) } });

    // Les lointaines : même géométrie, même matériau, une autre tranche de
    // profondeur et un rang de rendu derrière l'herbe.
    this._lucMatFond = new THREE.ShaderMaterial({
      uniforms: Object.assign({}, this._uniforms,
        { uProfBande: { value: new THREE.Vector2(-0.01, PROF_COUPURE) } }),
      vertexShader: lucioleVertexShader(this._opts),
      fragmentShader: lucioleFragmentShader(this._opts),
      transparent: true, depthTest: false, depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this._luciolesFond = new THREE.Points(g, this._lucMatFond);
    this._luciolesFond.frustumCulled = false;
    this._luciolesFond.renderOrder = 1;
    this._scene.add(this._luciolesFond);

    if (this._opts.halo) {
      // Le même nuage, une seconde passe. Un uniforme de taille différent
      // suffit : la géométrie et les attributs sont partagés, donc l'éclairage
      // local ne coûte qu'un draw call et des fragments.
      const uHalo = Object.assign({}, this._uniforms,
        { uTaillePx: this._uniforms.uHaloPx, uProfBande: { value: new THREE.Vector2(-0.01, 1.01) } });
      this._haloMat = new THREE.ShaderMaterial({
        uniforms: uHalo,
        vertexShader: lucioleVertexShader(this._opts),
        fragmentShader: haloFragmentShader,
        transparent: true, depthTest: false, depthWrite: false,
        blending: THREE.AdditiveBlending
      });
      this._halo = new THREE.Points(g, this._haloMat);
      this._halo.frustumCulled = false;
      this._halo.renderOrder = 0;
      this._scene.add(this._halo);
    }
    this._scene.add(this._lucioles);
  }

  _construireHerbes() {
    const B = this._opts.brins, S = this._opts.segments;
    // Un brin est une bande : S+1 rangées de deux sommets, expansée dans le
    // vertex shader. C'est l'idiome fat-line - three.js Line2 demanderait une
    // géométrie par largeur, ce qu'on évite précisément ici.
    const sommets = (S + 1) * 2;
    const pos = new Float32Array(sommets * 3);
    for (let k = 0; k <= S; k++) {
      const t = k / S;
      pos[k * 6] = -1; pos[k * 6 + 1] = t;
      pos[k * 6 + 3] = 1; pos[k * 6 + 4] = t;
    }
    const idx = [];
    for (let k = 0; k < S; k++) {
      const a = k * 2;
      idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setIndex(idx);
    const aX = new Float32Array(B), aHaut = new Float32Array(B);
    const aPenche = new Float32Array(B), aSeed = new Float32Array(B), aProf = new Float32Array(B);
    const aLarge = new Float32Array(B);
    for (let i = 0; i < B; i++) {
      // DEUX FOIS PLUS DE BRINS SUR LE BAS. La moitié des brins est tirée dans
      // le tiers inférieur du cadre et reste courte : c'est ce qui donne une
      // prairie DENSE au premier plan plutôt qu'une rangée de tiges isolées.
      // La profondeur suit, sinon les brins courts se retrouveraient au fond.
      const bas = (i % 2) === 0;
      aProf[i] = bas ? 0.62 + 0.38 * this._hasard() : this._hasard() * 0.75;
      aX[i] = (this._hasard() * 2 - 1) * 2.3;
      const t = this._hasard();
      aHaut[i] = bas
        ? HERBE_HAUT[0] * (0.45 + 0.55 * t)
        : HERBE_HAUT[0] + t * (HERBE_HAUT[1] - HERBE_HAUT[0]) * (0.55 + 0.45 * aProf[i]);
      aPenche[i] = (this._hasard() * 2 - 1) * 0.16;
      aSeed[i] = this._hasard();
      // DES LARGEURS VARIÉES : un brin d'herbe n'a pas la largeur de son
      // voisin, et c'est cette irrégularité qui empêche la touffe de lire
      // comme un peigne.
      aLarge[i] = 0.55 + 1.05 * this._hasard();
    }
    g.setAttribute('aX', new THREE.InstancedBufferAttribute(aX, 1));
    g.setAttribute('aHaut', new THREE.InstancedBufferAttribute(aHaut, 1));
    g.setAttribute('aPenche', new THREE.InstancedBufferAttribute(aPenche, 1));
    g.setAttribute('aSeed', new THREE.InstancedBufferAttribute(aSeed, 1));
    g.setAttribute('aProf', new THREE.InstancedBufferAttribute(aProf, 1));
    g.setAttribute('aLarge', new THREE.InstancedBufferAttribute(aLarge, 1));
    g.instanceCount = B;
    this._herbeGeo = g;
    this._herbeMat = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: herbeVertexShader,
      fragmentShader: herbeFragmentShader,
      depthTest: false, depthWrite: false, transparent: false,
      side: THREE.DoubleSide
    });
    this._herbes = new THREE.Mesh(g, this._herbeMat);
    this._herbes.frustumCulled = false;
    this._herbes.renderOrder = 2;   // devant le halo, derrière les lucioles
    this._scene.add(this._herbes);
  }

  _appliquerPalette(palette) {
    const vifs = (palette && palette.vividColors && palette.vividColors.length >= 1)
      ? palette.vividColors : [0x2a4a7a];
    let h = null;
    for (const v of vifs) { const x = teinteDe(v); if (x != null) { h = x; break; } }
    if (h == null) h = 0.60;
    // TRÈS DÉSATURÉE, et c'est un choix, pas une timidité : une nuit d'été n'a
    // pas de couleur franche, et le seul objet coloré de l'image doit être la
    // luciole. La palette donne une DOMINANTE au ciel, rien de plus.
    // LES CLARTÉS SONT ÉTALONNÉES SUR LA SORTIE. Mesuré sur la première
    // version : le cadre entier ressortait à 2.5 sur 255 - un écran noir avec
    // quelques lucioles, pas une prairie. Deux causes : des clartés posées au
    // jugé dans un espace linéaire où ACES et la courbe de sortie les écrasent,
    // et un dégradé de ciel au CARRÉ qui amenait la teinte la plus sombre sur
    // les deux tiers supérieurs du cadre. Les valeurs ci-dessous visent des
    // octets : ciel 16 à l'horizon et 7 au zénith, brume 32.
    poserHSL(this._uniforms.uCiel.value, h, 0.30, 0.125);
    poserHSL(this._uniforms.uCielHaut.value, h, 0.45, 0.062);
    poserHSL(this._uniforms.uBrume.value, h, 0.14, 0.205);
    // Le sol : la teinte du ciel, en plus sombre et plus bleu. Il doit ressortir
    // SOUS le ciel, pas au-dessus : c'est le fond noir dont les lucioles ont
    // besoin.
    poserHSL(this._uniforms.uSol.value, (h + 0.02) % 1, 0.40, 0.045);
    poserHSL(this._uniforms.uHerbe.value, (h + 0.08) % 1, 0.28, 0.105);
  }

  // LE PAS DE KURAMOTO, en O(N). Voir l'en-tête du fichier pour la raison.
  _kuramoto(pas, omegaMaitre) {
    const N = this._N;
    const phi = this._phi;
    // Le paramètre d'ordre : une seule passe, deux accumulateurs.
    let sc = 0, ss = 0, n = 0;
    for (let i = 0; i < N; i++) {
      if (this._vivante[i] < 0.5) continue;
      sc += Math.cos(phi[i]); ss += Math.sin(phi[i]); n++;
    }
    if (n === 0) return;
    sc /= n; ss /= n;
    const r = Math.sqrt(sc * sc + ss * ss);
    const psi = Math.atan2(ss, sc);
    this._r = r; this._psi = psi;

    const K = this._K;
    const Km = Math.max(0, K - K_BASE) * MAITRE_POIDS;
    this._phiMaitre = (this._phiMaitre + omegaMaitre * pas) % (Math.PI * 2);
    const pm = this._phiMaitre;
    const deuxPi = Math.PI * 2;
    for (let i = 0; i < N; i++) {
      if (this._vivante[i] < 0.5) continue;
      const p = phi[i];
      const d = this._omega[i] * omegaMaitre
        + K * r * Math.sin(psi - p)
        + Km * Math.sin(pm - p);
      let np = p + d * pas;
      // Repliement : sans lui la phase croît indéfiniment et finit par perdre
      // sa précision en flottant simple, ce qui se voit comme un clignotement
      // qui se dérègle après quelques minutes.
      np %= deuxPi; if (np < 0) np += deuxPi;
      phi[i] = np;
    }
  }

  update(deltaTime, profile) {
    // PAUSE : gel des phases. Une luciole qui s'éteint parce qu'on a appuyé
    // sur pause n'aurait aucun sens ; celle qui était allumée le reste.
    if (profile.playbackState !== 'PLAYING') return;

    this._temps += deltaTime;
    const u = this._uniforms;
    const director = profile.director;

    if (typeof window !== 'undefined') u.uDpr.value = Math.min(window.devicePixelRatio || 1, 2);

    const bandes = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    if (bandes) {
      this._medium += ((bandes.mid != null ? bandes.mid : 0.4) - this._medium) * Math.min(1, deltaTime / 0.35);
    }
    u.uVent.value = VENT_BASE + VENT_GAIN * clamp01(this._medium);

    if (profile.artworkPalette !== this._paletteVue) {
      this._paletteVue = profile.artworkPalette;
      this._appliquerPalette(profile.artworkPalette);
    }

    // --- LE COMPTE DE LUCIOLES suit la progression : 300 au début et à la
    // fin, le maximum du tier au milieu. Aucune allocation : on n'allume et
    // n'éteint que des entrées d'un tableau déjà dimensionné.
    const prog = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    const cible = Math.round(LUCIOLES_MIN + (this._opts.max - LUCIOLES_MIN) * Math.sin(Math.PI * prog));
    if (cible !== this._cible) {
      this._cible = cible;
      for (let i = 0; i < this._N; i++) this._vivante[i] = i < cible ? 1 : 0;
      this._lucGeo.getAttribute('aVivante').needsUpdate = true;
    }

    // --- LE COUPLAGE ---------------------------------------------------------
    const energie = director && director.intensity != null ? clamp01(director.intensity) : 0.3;
    this._energie += (energie - this._energie) * Math.min(1, deltaTime / 0.5);
    const mode = director ? director.mode : null;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const periode = bpm > 0 ? 60 / bpm : 1;
    if (mode === 'drop' && this._dernierMode !== 'drop') {
      this._dropJusqu = this._temps + DROP_MESURES * 4 * periode;
    }
    this._dernierMode = mode;
    const enDrop = this._temps < this._dropJusqu;
    this._K = this._kForce != null ? this._kForce
      : (enDrop ? K_DROP : K_BASE + K_GAIN * this._energie * this._energie);

    // SANS BPM, le maître bat à 1 Hz. Le comportement émergent est identique :
    // ce qui compte est le RAPPORT entre le couplage et la dispersion, pas la
    // fréquence elle-même.
    const omegaMaitre = (bpm > 0 ? bpm / 60 : BPM_DEFAUT_HZ) * Math.PI * 2;

    // Le pas est borné ET sous-divisé : un deltaTime de 200 ms en un seul pas
    // d'Euler sur un oscillateur fortement couplé dépasse la phase cible et
    // oscille au lieu de converger.
    let reste = Math.min(deltaTime, 0.25);
    while (reste > 1e-4) {
      const pas = Math.min(reste, PAS_MAX);
      this._kuramoto(pas, omegaMaitre);
      reste -= pas;
    }

    this._phiAttr.set(this._phi);
    this._lucGeo.getAttribute('aPhi').needsUpdate = true;

    u.uTemps.value = this._temps;
    u.uOrdre.value = this._r;
    this._majFilante(deltaTime);

    if (this._voirOrdre && this._temps - this._dernierTrace > 0.5) {
      this._dernierTrace = this._temps;
      // eslint-disable-next-line no-console
      console.log('[fireflies] r=' + this._r.toFixed(3) + '  K=' + this._K.toFixed(2)
        + '  energie=' + this._energie.toFixed(2) + '  n=' + this._cible
        + '  bpm=' + (bpm || 'aucun'));
    }
  }

  dispose() {
    this._scene.remove(this._fond);
    this._fondGeo.dispose(); this._fondMat.dispose();
    if (this._arbresTex) this._arbresTex.dispose();
    this._scene.remove(this._lucioles);
    this._scene.remove(this._luciolesFond);
    this._lucGeo.dispose(); this._lucMat.dispose(); this._lucMatFond.dispose();
    if (this._halo) { this._scene.remove(this._halo); this._haloMat.dispose(); }
    this._scene.remove(this._herbes);
    this._herbeGeo.dispose(); this._herbeMat.dispose();
  }
}
